// useFixMobileKeyboard.ts
import { useEffect } from "react";

/**
 * Fixes mobile keyboard issues in WebView:
 * - Prevents page jumping / overlap with status bar.
 * - Ensures input fields scroll smoothly into view.
 * - Handles 100vh resizing bugs.
 * - iOS-optimized to prevent flicker
 */
export const useFixMobileKeyboard = () => {
  useEffect(() => {
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (!isMobile) return;

    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    let resizeTimeout: NodeJS.Timeout;
    let initialHeight = window.innerHeight;

    // --- Handle scroll & viewport jump ---
    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      // iOS: Use shorter delay and instant scroll to prevent flicker
      const delay = isIOS ? 100 : 250;
      const behavior = isIOS ? "auto" : "smooth";

      setTimeout(() => {
        target.scrollIntoView({
          behavior: behavior as ScrollBehavior,
          block: "center",
        });
      }, delay);
    };

    const handleFocusIn = () => document.body.classList.add("keyboard-open");
    const handleFocusOut = () =>
      document.body.classList.remove("keyboard-open");

    window.addEventListener("focusin", handleFocusIn);
    window.addEventListener("focusout", handleFocusOut);

    document.addEventListener("focus", handleFocus, true);

    // --- Adjust 100vh issue dynamically with iOS optimization ---
    const updateVH = () => {
      // iOS: Debounce resize events to prevent flicker
      if (isIOS) {
        if (resizeTimeout) clearTimeout(resizeTimeout);

        resizeTimeout = setTimeout(() => {
          // Only update if height changed significantly (more than 100px)
          // This prevents minor viewport adjustments from triggering updates
          const currentHeight = window.innerHeight;
          if (Math.abs(currentHeight - initialHeight) > 100) {
            document.documentElement.style.setProperty(
              "--vh",
              `${currentHeight * 0.01}px`
            );
            initialHeight = currentHeight;
          }
        }, 150);
      } else {
        // Android: Update immediately
        document.documentElement.style.setProperty(
          "--vh",
          `${window.innerHeight * 0.01}px`
        );
      }
    };

    updateVH();
    window.addEventListener("resize", updateVH);

    return () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      window.removeEventListener("focusin", handleFocusIn);
      window.removeEventListener("focusout", handleFocusOut);
      document.removeEventListener("focus", handleFocus, true);
      window.removeEventListener("resize", updateVH);
    };
  }, []);
};
